import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { PostsService } from "../api/client";

const TABS = [
    { key: "pending", label: "Chờ duyệt" },
    { key: "approved", label: "Đã duyệt" },
];

export default function AdminPosts() {
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    // ---------- LOAD ----------
    const loadPosts = useCallback(async () => {
        setLoading(true);
        try {
            // ưu tiên gọi theo status
            let data = await PostsService.list({ status: tab, limit: 500 });

            // fallback nếu backend bỏ qua status
            if (!Array.isArray(data) || data.length === 0) {
                const all = await PostsService.list({ limit: 500 });
                data = Array.isArray(all) ? all.filter((p) => (p.status || "pending") === tab) : [];
            }

            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setRows([]);
            toast.error("Không tải được danh sách tin");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { loadPosts(); }, [loadPosts]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter((p) =>
            (p.title || "").toLowerCase().includes(s) ||
            (p.content || "").toLowerCase().includes(s) ||
            (p.address || "").toLowerCase().includes(s) ||
            String(p.service_id || "").includes(s)
        );
    }, [rows, q]);

    // ---------- ACTIONS ----------
    const quickToggleStatus = async (p) => {
        const next = p.status === "approved" ? "pending" : "approved";
        try {
            // ✅ endpoint mới: POST /api/posts/:id/status?status=...
            await PostsService.updateStatus(p.id, next);

            // cập nhật UI nhanh
            setRows((prev) =>
                next !== tab
                    ? prev.filter((x) => x.id !== p.id)
                    : prev.map((x) => (x.id === p.id ? { ...x, status: next } : x))
            );
            toast.success("Đã cập nhật trạng thái");
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.message || e?.message || "Không cập nhật được trạng thái");
        }
    };

    const removeItem = async (id) => {
        if (!window.confirm("Xoá tin này?")) return;
        try {
            // ✅ endpoint mới: DELETE /api/posts/:id
            await PostsService.remove(id);
            setRows((prev) => prev.filter((x) => x.id !== id));
            toast.success("Đã xoá");
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.message || e?.message || "Xoá thất bại");
        }
    };

    // ---------- UI ----------
    return (
        <div className="p-4 md:p-6">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">Quản lý tin đăng</h1>

            <div className="flex gap-2 mb-4">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`px-3 py-2 rounded-lg border text-sm ${tab === t.key ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-800 border-gray-200"
                            }`}
                    >
                        {t.label}
                    </button>
                ))}
                <div className="ml-auto">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Tìm theo tiêu đề, mô tả, dịch vụ…"
                        className="h-10 w-64 max-w-[60vw] rounded-lg border border-gray-300 px-3 text-sm outline-none focus:ring-2 focus:ring-red-500"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-gray-500">Đang tải…</div>
            ) : filtered.length === 0 ? (
                <div className="text-gray-500">Không có tin.</div>
            ) : (
                <ul className="space-y-4">
                    {filtered.map((p) => (
                        <li key={p.id} className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow transition">
                            <div className="p-4 md:p-5">
                                <div className="font-semibold text-gray-900 text-base md:text-lg">{p.title || "(Chưa có tiêu đề)"}</div>
                                <div className="text-sm text-gray-600 line-clamp-2">{p.content || "(Chưa có mô tả)"}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Dịch vụ: {p.service_id ?? "-"} • Trạng thái: {p.status}
                                </div>
                            </div>
                            <div className="px-4 md:px-5 pb-4 md:pb-5 flex items-center gap-2">
                                <button onClick={() => quickToggleStatus(p)} className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200">
                                    {p.status === "approved" ? "Chuyển về chờ duyệt" : "Duyệt nhanh"}
                                </button>
                                <button onClick={() => removeItem(p.id)} className="ml-auto px-3 py-2 rounded-lg text-sm bg-white border border-gray-300 hover:bg-gray-50">
                                    Xoá
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
